import { Edit, Map, NowBolt, Person, Search } from '@rover/icons';
import { Icon } from '@rover/icons/utils';
import TextWithSingleInlineLink from '@rover/kibble/official-patterns/TextWithSingleInlineLink';
import { validateParagraphText } from '@rover/kibble/official-patterns/TextWithSingleInlineLink/TextWithSingleInlineLink.common';
import { AlertSeverity } from '@rover/react-lib/src/components/notifications/Alert/Alert';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import { AnalyticsEvent } from '@rover/types/src/Analytics';

import { HeaderIconEnum } from './builders/constants';

export type SearchSectionHeaderProps = {
  id?: string;
  headerIconName?: string;
  headerText: string;
  infoIconOnClick?: () => void;
  primarySubheaderText?: string;
  secondarySubheaderText?: string;
  alertText?: string;
  alertSeverity?: AlertSeverity;
  alertLinkHref?: string;
  alertLinkEvent?: AnalyticsEvent;
};

export const getIcon = (iconName: SearchSectionHeaderProps['headerIconName']): Icon => {
  if (!iconName) {
    return Search;
  }

  if (typeof iconName === 'string') {
    switch (iconName) {
      case HeaderIconEnum.EDIT:
        return Edit;
      case HeaderIconEnum.MAP:
        return Map;
      case HeaderIconEnum.BOLT:
        return NowBolt;
      case HeaderIconEnum.PERSON:
        return Person;
      case HeaderIconEnum.SEARCH:
      default:
        return Search;
    }
  }

  return iconName;
};

export const getAlertElement = (
  alertText: SearchSectionHeaderProps['alertText'],
  alertLinkHref: SearchSectionHeaderProps['alertLinkHref'],
  alertLinkEvent: SearchSectionHeaderProps['alertLinkEvent'],
  getTextElementFunction: (string) => React.ReactNode
): React.ReactNode => {
  const onAlertLinkClick = (): void => {
    if (alertLinkEvent) {
      emitAnalyticsEvent(alertLinkEvent);
    }
  };

  try {
    validateParagraphText(alertText || '');

    // Text has link. Note: Markdown is not supported
    return (
      <TextWithSingleInlineLink
        size="100"
        textColor="secondary"
        target="_blank"
        sx={{ lineHeight: 21 }}
        asParagraph
        paragraphText={alertText || ''}
        href={alertLinkHref || ''}
        onClick={onAlertLinkClick}
        mb="0x"
      />
    );
  } catch (error) {
    // Text does not have link
    return getTextElementFunction(alertText);
  }
};

/**
 * Helper function to build the test ID. This allows us to more easily pull the
 * header based on the correct icon and type of header.
 */
export const buildTestId = (
  id: SearchSectionHeaderProps['id'],
  iconName: SearchSectionHeaderProps['headerIconName']
): string => {
  if (!id) {
    return 'search-results-header';
  }

  if (iconName) {
    return `${id}-${iconName.toLowerCase()}`;
  }

  return id;
};
